/* eslint-disable @typescript-eslint/consistent-type-assertions */
import type { WorkerPoolResource, WorkerPoolsSummaryResource, TenantResource, DynamicWorkerType } from "@octopusdeploy/octopus-server-client";
import { Permission, EndpointsHelper } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import type * as H from "history";
import { isEqual } from "lodash";
import * as React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useOnClearMachineDispatch } from "~/areas/infrastructure/hooks/useOnClearMachineDispatch";
import { repository } from "~/clientInstance";
import type { FilterSection } from "~/components/AdvancedFilterLayout";
import AdvancedFilterLayout, { AdvancedFilterCheckbox } from "~/components/AdvancedFilterLayout";
import { AdvancedFilterTextInput } from "~/components/AdvancedFilterLayout/Text/AdvancedFilterTextInput";
import type { DataBaseComponentState } from "~/components/DataBaseComponent/DataBaseComponent";
import { DataBaseComponent } from "~/components/DataBaseComponent/DataBaseComponent";
import Dialog from "~/components/Dialog/Dialog";
import FilterSearchBox from "~/components/FilterSearchBox/FilterSearchBox";
import FormPage from "~/components/FormPage/FormPage";
import { EndpointCommunicationStyleMultiSelect } from "~/components/MultiSelect/EndpointCommunicationStyleMultiSelect";
import { MachineModelHealthStatusMultiSelect } from "~/components/MultiSelect/MachineModelHealthStatusMultiSelect";
import { RoleMultiSelect } from "~/components/MultiSelect/RoleMultiSelect";
import { ShellNameMultiSelect } from "~/components/MultiSelect/ShellNameMultiSelect";
import { WorkerPoolMultiSelect } from "~/components/MultiSelect/WorkerPoolMultiSelect";
import InternalRedirect from "~/components/Navigation/InternalRedirect";
import { NoResults } from "~/components/NoResults/NoResults";
import { OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import type { PrimaryPageAction, PageAction } from "~/components/PageActions/PageActions";
import NumberedPagingBar from "~/components/PagingBaseComponent/NumberedPagingBar";
import PaperLayout from "~/components/PaperLayout/PaperLayout";
import { QueryStringFilters } from "~/components/QueryStringFilters/QueryStringFilters";
import { Section } from "~/components/Section/Section";
import ExpansionButtons from "~/components/form/Sections/ExpansionButtons";
import * as tenantTagsets from "~/components/tenantTagsets";
import type { TagIndex } from "~/components/tenantTagsets";
import MachineHealthStatusHelper from "~/utils/MachineHealthStatusHelper";
import RequestRaceConditioner from "~/utils/RequestRaceConditioner";
import PermissionCheck, { isAllowed } from "../../../../../components/PermissionCheck/PermissionCheck";
import InfrastructureLayout from "../../InfrastructureLayout";
import { InfrastructureLayoutBusy } from "../../InfrastructureLayout/InfrastructureLayout";
import ConfirmTentacleUpgradePanel from "../../MachinesLayout/ConfirmTentacleUpgradePanel";
import AddWorkerPoolsDialog from "./AddWorkerPoolsDialog";
import type { WorkerPoolSummaryMachineQuery } from "./WorkerPoolSummaryMachineQuery";
import WorkerPoolSummarySection from "./WorkerPoolSummarySection";
import WorkerPoolsSorter from "./WorkerPoolsSorter";
import type { WorkerPoolsSummaryFilter } from "./WorkerPoolsSummaryFilter";
import { defaultWorkerPoolsSummaryFilter, createWorkerPoolsSummaryArgs, workerPoolsFilterToQuery, workerPoolsQueryToFilter } from "./WorkerPoolsSummaryFilter";
class FilterLayout extends AdvancedFilterLayout<WorkerPoolsSummaryFilter> {
}
const WorkerPoolQueryStringFilters = QueryStringFilters.For<WorkerPoolsSummaryFilter, WorkerPoolSummaryMachineQuery>();
const PageSize = 20;
interface WorkerPoolsPageProps {
    spaceId: string;
}
interface WorkerPoolsLayoutInnerProps extends WorkerPoolsPageProps {
    initialData: InitialData;
    onClearMachine(): void;
    location: H.Location;
    history: H.History;
}
interface WorkerPoolLayoutState extends DataBaseComponentState {
    workerPoolsSummary: WorkerPoolsSummaryResource;
    filter: WorkerPoolsSummaryFilter;
    queryFilter?: WorkerPoolsSummaryFilter;
    isSearching?: boolean;
    workerPools: WorkerPoolResource[];
    machineRoles: string[];
    tenants: TenantResource[];
    tagIndex: TagIndex;
    hasDeploymentTargets: boolean;
    redirectToTasks: boolean;
    redirectToWorkerPoolId?: string;
    currentPageIndex: number;
    workerShellNames: string[];
    workerTypes: DynamicWorkerType[];
}
interface InitialData {
    workerPools: WorkerPoolResource[];
    machineRoles: string[];
    tagIndex: TagIndex;
    hasDeploymentTargets: boolean;
    workerShellNames: string[];
    workerPoolsSummary: WorkerPoolsSummaryResource;
    tenants: TenantResource[];
    workerTypes: DynamicWorkerType[];
}
const Title = "Worker pools";
const WorkerPoolsLayoutFormPage = FormPage<InitialData>();
export function WorkerPoolsPage({ spaceId }: WorkerPoolsPageProps) {
    const history = useHistory();
    const location = useLocation();
    const onClearMachine = useOnClearMachineDispatch();
    return (<WorkerPoolsLayoutFormPage title={Title} load={async () => {
            const defaultWorkerPoolSummaryArgs = createWorkerPoolsSummaryArgs(defaultWorkerPoolsSummaryFilter);
            const workerPools = repository.WorkerPools.all();
            const machineRoles = repository.MachineRoles.all();
            const workerShellNames = repository.WorkerShells.all();
            const workerPoolsSummary = repository.WorkerPools.summary(defaultWorkerPoolSummaryArgs);
            const tenants = isAllowed({ permission: Permission.TenantView, tenant: "*" }) ? repository.Tenants.all() : Promise.resolve([]);
            const tagIndex = tenantTagsets.getTagIndex();
            const workerTypes = repository.WorkerPools.getDynamicWorkerTypes();
            const machineCount = repository.Machines.list({ take: 0 });
            return {
                workerPools: await workerPools,
                machineRoles: await machineRoles,
                tagIndex: await tagIndex,
                hasDeploymentTargets: (await machineCount).TotalResults > 0,
                workerShellNames: await workerShellNames,
                workerPoolsSummary: await workerPoolsSummary,
                tenants: await tenants,
                workerTypes: await workerTypes,
            };
        }} renderWhenLoaded={(data) => <WorkerPoolsLayoutInternal initialData={data} onClearMachine={onClearMachine} location={location} history={history} spaceId={spaceId}/>} renderAlternate={(args) => <InfrastructureLayoutBusy title={Title} {...args}/>}/>);
}
class WorkerPoolsLayoutInternal extends DataBaseComponent<WorkerPoolsLayoutInnerProps, WorkerPoolLayoutState> {
    private machineHealthStatuses = MachineHealthStatusHelper.getMachineModelHealthStatusResources();
    private communicationStyles = EndpointsHelper.getCommunicationStyleResources();
    private requestRaceConditioner = new RequestRaceConditioner();
    constructor(props: WorkerPoolsLayoutInnerProps) {
        super(props);
        this.state = {
            workerPoolsSummary: props.initialData.workerPoolsSummary,
            filter: defaultWorkerPoolsSummaryFilter,
            isSearching: false,
            machineRoles: props.initialData.machineRoles,
            workerPools: props.initialData.workerPools,
            tenants: props.initialData.tenants,
            tagIndex: props.initialData.tagIndex,
            hasDeploymentTargets: false,
            currentPageIndex: 0,
            workerShellNames: props.initialData.workerShellNames,
            workerTypes: props.initialData.workerTypes,
            redirectToTasks: false,
        };
    }
    async UNSAFE_componentDidMount() {
        // Clear currentMachine (to avoid seeing old machine data when switching machines).
        this.props.onClearMachine();
    }
    render() {
        if (this.state.redirectToTasks) {
            return <InternalRedirect to={links.tasksPage.generateUrl()} push={true}/>;
        }
        if (this.state.redirectToWorkerPoolId) {
            return <InternalRedirect to={links.editWorkerPoolPage.generateUrl({ spaceId: this.props.spaceId, workerPoolId: this.state.redirectToWorkerPoolId })} push={true}/>;
        }
        const hasWorkerPool = this.state.workerPools && this.state.workerPools.length > 0;
        const addWorkerPageAction: PageAction = {
            type: "navigate",
            label: "Add worker",
            path: links.newWorkerMachinePage.generateUrl({ spaceId: this.props.spaceId }),
            buttonType: "secondary",
            hasPermissions: isAllowed({
                permission: Permission.WorkerEdit,
            }),
        };
        const addWorkerPoolPageAction: PrimaryPageAction = {
            type: "dialog",
            label: "Add Worker Pool",
            hasPermissions: isAllowed({
                permission: Permission.WorkerEdit,
            }),
            renderDialog: ({ isOpen, closeDialog }) => {
                return (<Dialog open={isOpen} onRequestClose={closeDialog}>
                        <AddWorkerPoolsDialog saveDone={async (workerPool) => {
                        this.setState({ redirectToWorkerPoolId: workerPool.Id });
                    }}/>
                    </Dialog>);
            },
        };
        const overflowMenuReorderItem = OverflowMenuItems.dialogItem("Reorder", <WorkerPoolsSorter saveDone={this.refreshWorkerPoolSummaryData}/>, { permission: Permission.WorkerView });
        let workerPoolExpanders: React.ReactNode = null;
        const workerPoolSummaries = this.state.workerPoolsSummary && this.state.workerPoolsSummary.WorkerPoolSummaries;
        if (workerPoolSummaries) {
            const start = this.state.currentPageIndex === 0 ? 0 : this.state.currentPageIndex * PageSize;
            const end = start + PageSize;
            workerPoolExpanders = workerPoolSummaries.slice(start, end).map((workerPoolsSummary) => {
                return (<WorkerPoolSummarySection key={workerPoolsSummary.WorkerPool.Id} workerPoolSummary={workerPoolsSummary} workerTypes={this.state.workerTypes} filter={this.state.filter} isFiltering={this.isFiltering()} tenants={this.props.initialData.tenants} tagIndex={this.state.tagIndex}/>);
            });
        }
        if (this.state.workerPoolsSummary && this.state.workerPoolsSummary.WorkerPoolSummaries.length === 0) {
            workerPoolExpanders = (<Section>
                    <NoResults />
                </Section>);
        }
        const filterSections: FilterSection[] = [
            {
                render: (<div>
                        <AdvancedFilterCheckbox label="Disabled only" value={this.state.filter.isDisabled} onChange={(x) => {
                        this.setFilterState({ isDisabled: x }, () => {
                            this.onFilterChange();
                        });
                    }}/>
                        <AdvancedFilterCheckbox label="Hide empty worker pools" value={this.state.filter.hideEmptyWorkerPools} onChange={(x) => {
                        this.setFilterState({ hideEmptyWorkerPools: x }, () => {
                            this.onFilterChange();
                        });
                    }}/>
                        <AdvancedFilterTextInput fieldName={"worker"} value={this.state.filter.machinePartialName || ""} onChange={(x) => {
                        this.setFilterState({ machinePartialName: x }, () => {
                            this.onFilterChange();
                        });
                    }}/>
                        <WorkerPoolMultiSelect items={this.state.workerPools ? this.state.workerPools : []} value={this.state.filter.workerPoolIds} onChange={(x) => {
                        this.setFilterState({ workerPoolIds: x }, () => {
                            this.onFilterChange();
                        });
                    }}/>
                        <RoleMultiSelect items={this.state.machineRoles ? this.state.machineRoles : []} value={this.state.filter.roles} onChange={(x) => {
                        this.setFilterState({ roles: x }, () => {
                            this.onFilterChange();
                        });
                    }}/>
                        <MachineModelHealthStatusMultiSelect items={this.machineHealthStatuses} value={this.state.filter.healthStatuses} onChange={(x) => {
                        this.setFilterState({ healthStatuses: x }, () => {
                            this.onFilterChange();
                        });
                    }}/>
                        <EndpointCommunicationStyleMultiSelect items={this.communicationStyles} value={this.state.filter.commStyles} onChange={(x) => {
                        this.setFilterState({ commStyles: x }, () => {
                            this.onFilterChange();
                        });
                    }}/>
                        <ShellNameMultiSelect items={this.state.workerShellNames ? this.state.workerShellNames : []} value={this.state.filter.shellNames} onChange={(x) => {
                        this.setFilterState({ shellNames: x }, this.onFilterChange);
                    }}/>
                    </div>),
            },
        ];
        const tentacleUpgradesRequiredWarning = workerPoolSummaries && workerPoolSummaries.length > 0 && workerPoolSummaries.find((x) => x.TentacleUpgradesRequired === true) && (<PermissionCheck permission={Permission.WorkerEdit}>
                <ConfirmTentacleUpgradePanel doBusyTask={this.doBusyTask} calloutDescriptionElement={<p>One or more workers are running old versions of the Tentacle agent and can be upgraded.</p>} onTentacleUpgradeComplete={() => {
                this.setState({ redirectToTasks: true });
            }}/>
            </PermissionCheck>);
        return (<InfrastructureLayout {...this.props}>
                <WorkerPoolQueryStringFilters filter={this.state.filter} getQuery={workerPoolsFilterToQuery} getFilter={workerPoolsQueryToFilter} onFilterChange={(filter) => this.setState({ filter, queryFilter: filter }, () => this.onFilterChange())}/>
                <PaperLayout busy={this.state.busy} errors={this.errors} title={Title} primaryAction={addWorkerPoolPageAction} pageActions={hasWorkerPool ? [addWorkerPageAction] : undefined} overflowActions={hasWorkerPool ? [overflowMenuReorderItem] : undefined}>
                    {workerPoolSummaries && (<div>
                            {tentacleUpgradesRequiredWarning}
                            <FilterLayout filterSections={filterSections} filter={this.state.filter} queryFilter={this.state.queryFilter} defaultFilter={defaultWorkerPoolsSummaryFilter} initiallyShowFilter={this.isFiltering()} additionalHeaderFilters={[
                    <FilterSearchBox placeholder={"Search worker pools..."} value={this.state.filter.partialName} onChange={(x) => {
                            this.setFilterState({ partialName: x }, () => {
                                this.onFilterChange();
                            });
                        }} autoFocus={true}/>,
                ]} onFilterReset={(filter: WorkerPoolsSummaryFilter) => {
                    this.setState({ filter }, () => {
                        this.onFilterChange();
                        const location = { ...this.props.history, search: "" };
                        this.props.history.replace(location);
                    });
                }} renderContent={() => (<div>
                                        <ExpansionButtons />
                                        {workerPoolExpanders}
                                        {this.state.workerPoolsSummary && this.state.workerPoolsSummary.WorkerPoolSummaries.length > 0 && (<NumberedPagingBar currentPageIndex={this.state.currentPageIndex} totalItems={this.state.workerPoolsSummary.WorkerPoolSummaries.length} pageSize={PageSize} onPageSelected={(_, currentPageIndex) => this.setState({ currentPageIndex })}/>)}
                                    </div>)}/>
                        </div>)}
                </PaperLayout>
            </InfrastructureLayout>);
    }
    private setFilterState<K extends keyof WorkerPoolsSummaryFilter>(state: Pick<WorkerPoolsSummaryFilter, K>, callback?: () => void) {
        this.setState((prev) => ({
            filter: { ...(prev.filter as object), ...(state as object) },
        }), callback);
    }
    private isFiltering() {
        return !isEqual(this.state.filter, defaultWorkerPoolsSummaryFilter);
    }
    private onFilterChange() {
        this.setState({ isSearching: true }, async () => {
            await this.refreshWorkerPoolSummaryData();
            this.setState({ isSearching: false });
        });
    }
    private refreshWorkerPoolSummaryData = async () => {
        await this.doBusyTask(() => this.loadWorkerPoolSummaries());
    };
    private async loadWorkerPoolSummaries() {
        const workerPoolSummariesArgs = createWorkerPoolsSummaryArgs(this.state.filter);
        await this.requestRaceConditioner.avoidStaleResponsesForRequest(repository.WorkerPools.summary(workerPoolSummariesArgs), (response) => {
            this.setState({
                workerPoolsSummary: response as WorkerPoolsSummaryResource,
            });
        });
    }
    static displayName = "WorkerPoolsLayoutInternal";
}
